import React, { useEffect, useMemo, useState } from 'react';
import { CalendarEvent, ChevronLeft, ChevronRight, Gift, StarFill } from 'react-bootstrap-icons';
import { useLocale } from '../i18n/LocaleContext';
import { fetchChallengeCalendar } from '../api/brands';
import {
  PARTNER_STATUSES,
  TIERS,
  budapestToday,
  monthOf,
  shiftMonth,
  monthGrid,
  challengeTitle,
  rewardMagnitude,
} from './challengeCalendar';
import './ChallengeCalendarTab.scss';

const LOCALES = { hu: 'hu-HU', en: 'en-GB' };

function fmt(lang, options, y, m, d = 1) {
  return new Intl.DateTimeFormat(LOCALES[lang] || 'en-GB', { ...options, timeZone: 'UTC' })
    .format(new Date(Date.UTC(y, m - 1, d)));
}

const monthLabel = (month, lang) => {
  const [y, m] = month.split('-').map(Number);
  return fmt(lang, { year: 'numeric', month: 'long' }, y, m);
};

const dayLabel = (date, lang) => {
  const [y, m, d] = date.split('-').map(Number);
  return fmt(lang, { month: 'short', day: 'numeric' }, y, m, d);
};

const longDayLabel = (date, lang) => {
  const [y, m, d] = date.split('-').map(Number);
  return fmt(lang, { month: 'long', day: 'numeric', weekday: 'long' }, y, m, d);
};

const timeLabel = (iso, lang) =>
  new Intl.DateTimeFormat(LOCALES[lang] || 'en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Budapest' }).format(new Date(iso));

// 2024-01-01 was a Monday.
const weekdayLabels = (lang) => Array.from({ length: 7 }, (_, i) => fmt(lang, { weekday: 'short' }, 2024, 1, 1 + i));

const isOwnDay = (c) => c.is_own_featured || c.has_own_reward;

/**
 * Eseménynaptár — every community challenge (D11), read-only: goal, timing,
 * progress and result, and every brand's rewards. The brand's own partner days
 * and rewards are highlighted; bot drafts and not-yet-final days carry a
 * "planned, may change" badge. Cancelled days never reach the portal, and
 * other brands' offer internals (caps, remaining stock) are not in the API.
 */
export default function ChallengeCalendarTab({ brand, onOpenPrizePool = null }) {
  const { t, lang } = useLocale();
  const [month, setMonth] = useState(() => monthOf(budapestToday()));
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [openDate, setOpenDate] = useState(null);

  const grid = useMemo(() => monthGrid(month), [month]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchChallengeCalendar(brand.id, { from: grid.from, to: grid.to })
      .then((data) => {
        if (cancelled) return;
        setCalendar(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [brand.id, grid]);

  const days = useMemo(() => new Map((calendar?.days || []).map((d) => [d.date, d])), [calendar]);
  const today = calendar?.today || budapestToday();
  const planning = calendar?.planning_window;
  const thisMonth = monthOf(today);
  const weekdays = useMemo(() => weekdayLabels(lang), [lang]);
  const openChallenges = openDate ? days.get(openDate)?.challenges || [] : [];

  return (
    <div className="panel-card cal">
      <div className="panel-card__row">
        <div>
          <h2 className="panel-card__title">{t('eventCalendar.title')}</h2>
          <p className="cal__lead">{t('eventCalendar.lead')}</p>
        </div>
        <div className="cal__nav">
          <button type="button" className="cal__nav-btn" onClick={() => setMonth((m) => shiftMonth(m, -1))} aria-label={t('eventCalendar.prevMonth')} title={t('eventCalendar.prevMonth')}>
            <ChevronLeft size={15} />
          </button>
          <span className="cal__month" aria-live="polite">{monthLabel(month, lang)}</span>
          <button type="button" className="cal__nav-btn" onClick={() => setMonth((m) => shiftMonth(m, 1))} aria-label={t('eventCalendar.nextMonth')} title={t('eventCalendar.nextMonth')}>
            <ChevronRight size={15} />
          </button>
          <button type="button" className="cal__today-btn" onClick={() => setMonth(thisMonth)} disabled={month === thisMonth}>
            {t('eventCalendar.today')}
          </button>
        </div>
      </div>

      <div className="cal__legend">
        {planning && (
          <span className="cal__legend-item">
            <span className="cal__swatch cal__swatch--window" />
            {t('eventCalendar.planningWindow', { from: dayLabel(planning.from, lang), to: dayLabel(planning.to, lang) })}
          </span>
        )}
        <span className="cal__legend-item">
          <span className="cal__swatch cal__swatch--today" />
          {t('eventCalendar.todayMarker')}
        </span>
        {PARTNER_STATUSES.map((status) => (
          <span key={status} className="cal__legend-item">
            <span className={`cal__dot cal__dot--${status}`} />
            {t(`eventCalendar.status.${status}`)}
          </span>
        ))}
        {TIERS.map((tier) => (
          <span key={tier} className="cal__legend-item">
            <span className={`cal__tier-bar cal__tier-bar--${tier}`} />
            {t(`eventCalendar.tier.${tier}`)}
          </span>
        ))}
        <span className="cal__legend-item"><span className="cal__may-change">{t('eventCalendar.mayChange')}</span></span>
        <span className="cal__legend-item"><span className="cal__own-swatch" />{t('eventCalendar.ownLegend')}</span>
      </div>

      {error && <div className="panel-card__err cal__error" role="alert">{t('eventCalendar.loadError')}</div>}

      {!error && calendar && calendar.total === 0 && (
        <div className="cal__empty">
          <CalendarEvent size={24} className="cal__empty-icon" />
          <div>
            <div className="cal__empty-title">{t('eventCalendar.empty')}</div>
            <div className="cal__empty-body">{t('eventCalendar.emptyHint')}</div>
          </div>
        </div>
      )}

      <div className={`cal__grid${loading ? ' is-loading' : ''}`} role="grid" aria-busy={loading} aria-label={monthLabel(month, lang)}>
        <div className="cal__row cal__row--head" role="row">
          {weekdays.map((label, i) => (
            <div key={label} role="columnheader" className={`cal__weekday${i >= 5 ? ' is-weekend' : ''}`}>{label}</div>
          ))}
        </div>
        {grid.weeks.map((week) => (
          <div key={week[0]} className="cal__row" role="row">
            {week.map((date) => {
              const day = days.get(date);
              const challenges = day?.challenges || [];
              const inWindow = day ? day.in_planning_window : Boolean(planning && date >= planning.from && date <= planning.to);
              const isToday = date === today;
              const classes = [
                'cal__day',
                monthOf(date) !== month && 'is-out',
                date < today && 'is-past',
                isToday && 'is-today',
                inWindow && 'is-window',
                challenges.some(isOwnDay) && 'is-own',
                openDate === date && 'is-open',
              ].filter(Boolean).join(' ');
              return (
                <div
                  key={date}
                  className={classes}
                  role="gridcell"
                  data-date={date}
                  aria-current={isToday ? 'date' : undefined}
                  tabIndex={challenges.length ? 0 : -1}
                  onClick={() => challenges.length && setOpenDate((d) => (d === date ? null : date))}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && challenges.length) {
                      e.preventDefault();
                      setOpenDate((d) => (d === date ? null : date));
                    }
                  }}
                >
                  <div className="cal__day-head">
                    <span className="cal__day-num">{Number(date.slice(8))}</span>
                    {isToday && <span className="cal__today-tag">{t('eventCalendar.todayMarker')}</span>}
                  </div>
                  {challenges.map((challenge) => (
                    <div
                      key={challenge.id}
                      className={`cal__chip cal__chip--${challenge.status} cal__chip--tier-${challenge.difficulty_tier || 'normal'}${isOwnDay(challenge) ? ' is-own' : ''}`}
                      title={`${t(`eventCalendar.status.${challenge.status}`)} · ${t(`eventCalendar.tier.${challenge.difficulty_tier || 'normal'}`)}`}
                    >
                      <span className="cal__chip-title">{challengeTitle(challenge, lang) || t(`eventCalendar.status.${challenge.status}`)}</span>
                      <span className="cal__chip-meta">
                        {challenge.may_change && <span className="cal__may-change">{t('eventCalendar.mayChange')}</span>}
                        {challenge.has_reward && <Gift size={11} className={`cal__gift${challenge.has_own_reward ? ' is-own' : ''}`} aria-label={t('eventCalendar.hasReward')} />}
                        {challenge.reward_mechanic && challenge.reward_mechanic !== 'none' && (
                          <span className={`cal__mech cal__mech--${challenge.reward_mechanic}`}>{t(`eventCalendar.mechanic.short.${challenge.reward_mechanic}`)}</span>
                        )}
                        {challenge.featured_brands.map((b) => (
                          <span key={b.brand_id} className={`cal__brand-chip${b.is_own ? ' is-own' : ''}`}>{b.name || '—'}</span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {openDate && openChallenges.length > 0 && (
        <section className="cal__detail" aria-label={longDayLabel(openDate, lang)}>
          <h3 className="cal__detail-date">{longDayLabel(openDate, lang)}</h3>
          {openChallenges.map((c) => (
            <article key={c.id} className={`cal__card${isOwnDay(c) ? ' is-own' : ''}`}>
              <div className="cal__card-head">
                <strong>{challengeTitle(c, lang) || t(`eventCalendar.status.${c.status}`)}</strong>
                <span className={`cal__status cal__status--${c.status}`}>{t(`eventCalendar.status.${c.status}`)}</span>
                <span className={`cal__tier cal__tier--${c.difficulty_tier || 'normal'}`}>{t(`eventCalendar.tier.${c.difficulty_tier || 'normal'}`)}</span>
                {c.may_change && <span className="cal__may-change">{t('eventCalendar.mayChange')}</span>}
                {c.is_own_featured && <span className="cal__own-badge"><StarFill size={10} /> {t('eventCalendar.yourPartnerDay')}</span>}
                {c.reward_mechanic && <span className={`cal__mech cal__mech--${c.reward_mechanic}`} data-testid="partner-mechanic">{t(`eventCalendar.mechanic.${c.reward_mechanic}`)}</span>}
              </div>
              {c.status === 'completed' && (
                <p className="cal__completed">{t(c.completed_early ? 'eventCalendar.mechanic.completedEarly' : 'eventCalendar.mechanic.completed')}</p>
              )}
              {c.description && <p className="cal__card-desc">{challengeTitle({ title: c.description }, lang)}</p>}
              <dl className="cal__facts">
                {c.metric && (
                  <>
                    <dt>{t('eventCalendar.goal')}</dt>
                    <dd>{t(`eventCalendar.metric.${c.metric}`)}{c.target_count ? ` · ${c.target_count.toLocaleString(LOCALES[lang])}` : ''}</dd>
                  </>
                )}
                {c.window && (
                  <>
                    <dt>{t('eventCalendar.when')}</dt>
                    <dd>{`${timeLabel(c.window.starts_at, lang)} – ${timeLabel(c.window.ends_at, lang)}`}</dd>
                  </>
                )}
                {c.target_count > 0 && (c.status === 'live' || c.status === 'completed' || c.status === 'failed') && (
                  <>
                    <dt>{t('eventCalendar.progressLabel')}</dt>
                    <dd>{t('eventCalendar.progress', { pct: Math.min(100, Math.round((c.progress_count / c.target_count) * 100)) })}</dd>
                  </>
                )}
                {c.reward_mechanic && c.reward_mechanic !== 'none' && c.mechanic_terms && (
                  <>
                    <dt>{t('eventCalendar.mechanic.label')}</dt>
                    <dd>{t(`eventCalendar.mechanic.terms.${c.reward_mechanic === 'gift_drop' && c.mechanic_terms.drop_audience === 'anyone_present' ? 'gift_drop_anyone' : c.reward_mechanic}`, {
                      n: c.mechanic_terms.prizes ?? c.mechanic_terms.drop_qty ?? c.mechanic_terms.max_eligible ?? '—',
                    })}</dd>
                  </>
                )}
                {c.featured_brands.length > 0 && (
                  <>
                    <dt>{t('eventCalendar.partnerDay')}</dt>
                    <dd>{c.featured_brands.map((b) => b.name).join(', ')}</dd>
                  </>
                )}
              </dl>
              <h4 className="cal__rewards-title"><Gift size={13} /> {t('eventCalendar.rewards')}</h4>
              {c.reward_items.length === 0 ? (
                <p className="cal__muted">{t('eventCalendar.noRewards')}</p>
              ) : (
                <ul className="cal__rewards">
                  {c.reward_items.map((item, i) => (
                    <li key={item.id || `${item.brand_id}-${item.type}-${i}`} className={`cal__reward${item.is_own ? ' is-own' : ''}`}>
                      <span className="cal__reward-brand">{item.funded_by === 'outfino' ? 'Outfino' : item.brand_name || '—'}</span>
                      <span>{t(`eventCalendar.rewardType.${item.type}`)}</span>
                      <span className="cal__reward-size">{rewardMagnitude(item, lang)}</span>
                      <span className="cal__muted">{t('eventCalendar.quantity', { n: item.quantity })}</span>
                      {item.is_own && <span className="cal__own-badge">{t('eventCalendar.yourReward')}{item.status ? ` · ${t(`eventCalendar.rewardStatus.${item.status}`)}` : ''}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </section>
      )}

      <p className="cal__soon">
        <Gift size={14} /> {t('eventCalendar.prizePoolLead')}{' '}
        {onOpenPrizePool && (
          <button type="button" className="cal__soon-link" onClick={onOpenPrizePool}>{t('eventCalendar.prizePoolLink')}</button>
        )}
      </p>
    </div>
  );
}
